import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { HelpCircle, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * FAQ Section Component
 * 
 * Displays FAQs grouped by category with collapsible/accordion UI.
 * Handles loading states and empty states gracefully.
 */
const FAQSection = ({ faqs, loading = false }) => {
  // Don't render if no FAQs and not loading
  if (!loading && (!faqs || Object.keys(faqs).length === 0)) {
    return null;
  }

  if (loading) {
    return <FAQLoadingState />;
  }

  const categories = Object.keys(faqs).filter((c) => Array.isArray(faqs[c]));
  const totalFAQs = categories.reduce((total, c) => total + faqs[c].length, 0);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <HelpCircle className="h-6 w-6 text-blue-600" />
          <div>
            <CardTitle>Frequently Asked Questions</CardTitle>
            <p className="text-sm text-gray-500 mt-1">
              {totalFAQs} question{totalFAQs !== 1 ? 's' : ''} across {categories.length} categor{categories.length !== 1 ? 'ies' : 'y'}
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {categories.map((category) => (
            <FAQCategory
              key={category}
              category={category}
              questions={faqs[category]}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

/**
 * FAQ Category Component
 * 
 * Displays a category of FAQs with a badge header and accordion-style questions.
 */
const FAQCategory = ({ category, questions }) => {
  if (!questions || questions.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      {/* Category Header */}
      <div className="flex items-center gap-2">
        <Badge variant="secondary" className="text-sm font-medium">
          {category}
        </Badge>
        <span className="text-xs text-gray-500">
          {questions.length} question{questions.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* FAQ Items */}
      <div className="space-y-2">
        {questions.map((faq) => (
          <FAQItem
            key={faq.id}
            question={faq.question}
            answer={faq.answer}
          />
        ))}
      </div>
    </div>
  );
};

/**
 * FAQ Item Component
 * 
 * Individual FAQ with collapsible answer. Uses custom accordion implementation
 * with smooth transitions and proper touch targets for mobile.
 */
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleOpen = () => setIsOpen(!isOpen);

  return (
    <div className="border border-gray-200 rounded-lg bg-white hover:shadow-sm transition-shadow">
      {/* Question Header - Clickable */}
      <button
        onClick={toggleOpen}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors rounded-lg min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
        aria-expanded={isOpen}
        aria-controls={`faq-answer-${question}`}
      >
        <div className="flex-1 pr-4">
          <h3 className="font-medium text-gray-900 leading-relaxed">
            {question}
          </h3>
        </div>
        <div className="flex-shrink-0">
          {isOpen ? (
            <ChevronUp className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-500" />
          )}
        </div>
      </button>

      {/* Answer Content - Collapsible */}
      <div
        id={`faq-answer-${question}`}
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 pb-4">
          <div className="text-gray-600 leading-relaxed whitespace-pre-line">
            {answer}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * FAQ Loading State Component
 * 
 * Skeleton loader for FAQ section with proper mobile responsive design.
 */
const FAQLoadingState = () => {
  return (
    <Card aria-busy="true">
      <CardHeader role="status" aria-live="polite" aria-atomic="true">
        <div className="flex items-center gap-3">
          <Skeleton className="h-6 w-6 rounded-full" />
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-32" />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Category 1 */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Skeleton className="h-6 w-20 rounded-full" />
              <Skeleton className="h-4 w-16" />
            </div>
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-5 w-5" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Category 2 */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Skeleton className="h-6 w-24 rounded-full" />
              <Skeleton className="h-4 w-16" />
            </div>
            <div className="space-y-2">
              {[1, 2].map((i) => (
                <div key={i} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-5 w-2/3" />
                    <Skeleton className="h-5 w-5" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default FAQSection;